import type { StoreProductionHistory } from "../../services/StoreProductionHistoryService";

export type StoreProductionStatus = "normal" | "low" | "issue" | "missing" | "no_baseline";

export interface StoreProductionSummary {
  storeId: string;
  latest: number | null;
  baseline: number | null;
  deltaPercent: number | null;
  status: StoreProductionStatus;
  values: Array<number | null>;
  categoriesFound: number | null;
  categoriesExpected: number | null;
}

export function median(values: number[]): number | null {
  const finite = values.filter(Number.isFinite).sort((a, b) => a - b);
  if (finite.length === 0) return null;
  const middle = Math.floor(finite.length / 2);
  return finite.length % 2 === 0
    ? (finite[middle - 1] + finite[middle]) / 2
    : finite[middle];
}

export function classifyStoreProduction(
  latest: number | null,
  baseline: number | null,
): StoreProductionStatus {
  if (latest === null) return "missing";
  if (baseline === null || baseline <= 0) return latest === 0 ? "issue" : "no_baseline";
  const ratio = latest / baseline;
  if (ratio < 0.5) return "issue";
  if (ratio < 0.75) return "low";
  return "normal";
}

export function buildStoreProductionSummaries(
  history: StoreProductionHistory,
  storeIds: string[],
): StoreProductionSummary[] {
  const latestSnapshot = [...history.snapshots].reverse().find((snapshot) => snapshot.available);
  const latestDate = latestSnapshot?.date;
  return storeIds.map((storeId) => {
    const values = history.snapshots.map((snapshot) => {
      if (!snapshot.available) return null;
      return snapshot.stores.find((store) => store.storeId === storeId)?.productsUploaded ?? null;
    });
    const latestStore = latestSnapshot?.stores.find((store) => store.storeId === storeId);
    const latest = latestStore?.productsUploaded ?? null;
    const historical = history.snapshots
      .filter((snapshot) => snapshot.available && snapshot.date !== latestDate)
      .map((snapshot) => snapshot.stores.find((store) => store.storeId === storeId)?.productsUploaded)
      .filter((value): value is number => value !== undefined && value > 0);
    const baseline = median(historical);
    const deltaPercent = latest !== null && baseline !== null && baseline > 0
      ? ((latest - baseline) / baseline) * 100
      : null;
    return {
      storeId,
      latest,
      baseline,
      deltaPercent,
      status: classifyStoreProduction(latest, baseline),
      values,
      categoriesFound: latestStore?.categoriesFound ?? null,
      categoriesExpected: latestStore?.categoriesExpected ?? null,
    };
  }).sort((left, right) => {
    const rank: Record<StoreProductionStatus, number> = {
      issue: 0,
      missing: 1,
      low: 2,
      no_baseline: 3,
      normal: 4,
    };
    return rank[left.status] - rank[right.status] || left.storeId.localeCompare(right.storeId, undefined, { numeric: true });
  });
}

// uniqueProducts is what the products page lists: the same UPC at several
// stores is one row there. A single store's rows are already unique; across all
// of a user's stores the snapshot carries the merged count; any other mix of
// stores has no merged count.
export function buildAggregateStoreProduction(
  history: StoreProductionHistory,
  storeIds: string[],
  useUserUniqueProducts = false,
) {
  return history.snapshots.map((snapshot) => {
    if (!snapshot.available) {
      return { date: snapshot.date, products: null, uniqueProducts: null, storesReported: 0, storesExpected: storeIds.length };
    }
    const selected = new Set(storeIds);
    const rows = snapshot.stores.filter((store) => selected.has(store.storeId));
    const products = rows.reduce((sum, store) => sum + store.productsUploaded, 0);
    const uniqueProducts = storeIds.length === 1
      ? (rows.length > 0 ? products : null)
      : useUserUniqueProducts ? snapshot.uniqueProducts ?? null : null;
    return {
      date: snapshot.date,
      products,
      uniqueProducts,
      storesReported: rows.length,
      storesExpected: storeIds.length,
    };
  });
}
